"use client";

import Image from "next/image";
import Link from "next/link";
import {
  Sparkles,
  Users,
  Calendar,
  TrendingUp,
  Target,
  Star,
  Package,
  Briefcase,
  FileText,
  ArrowRight,
  CheckCircle2,
  Zap,
  Clock,
  Shield,
} from "lucide-react";

// Pulse kategorileri
const CATEGORIES = [
  {
    icon: Users,
    name: "Çalışan Analizi",
    count: 7,
    color: "indigo",
    examples: ["Toplam çalışan", "Departman dağılımı", "Cinsiyet analizi"],
  },
  {
    icon: Calendar,
    name: "İzin Takibi",
    count: 5,
    color: "purple",
    examples: ["Bekleyen onaylar", "İzin kullanımı", "İzin türleri"],
  },
  {
    icon: TrendingUp,
    name: "Bordro Analizi",
    count: 3,
    color: "emerald",
    examples: ["Maaş özeti", "Departman bazlı", "Top maaşlar"],
  },
  {
    icon: Target,
    name: "Hedef Takibi",
    count: 4,
    color: "amber",
    examples: ["Aktif hedefler", "Gecikmiş", "Tamamlanma oranı"],
  },
  {
    icon: Star,
    name: "Performans",
    count: 3,
    color: "pink",
    examples: ["Ortalama skor", "Departman bazlı", "Değerlendirmeler"],
  },
  {
    icon: Package,
    name: "Zimmet Yönetimi",
    count: 3,
    color: "cyan",
    examples: ["Aktif zimmetler", "Türler", "İade bekleyenler"],
  },
  {
    icon: Briefcase,
    name: "İşe Alım",
    count: 4,
    color: "orange",
    examples: ["Aday sayısı", "Durumlar", "Açık pozisyonlar"],
  },
  {
    icon: FileText,
    name: "Avans Takibi",
    count: 3,
    color: "rose",
    examples: ["Bekleyen", "Ödenen", "Geçmiş talepler"],
  },
];

// Roadmap fazları
const ROADMAP = [
  {
    phase: "Phase 1",
    status: "completed",
    title: "Template-Based Analytics",
    description: "32 hazır analiz şablonu, %100 doğruluk",
    features: [
      "Doğal dil sorgulama",
      "8 kategori desteği",
      "Anlık sonuçlar",
      "Güvenli veri erişimi",
    ],
  },
  {
    phase: "Phase 2",
    status: "in-progress",
    title: "Intent Classification",
    description: "Akıllı şablon seçimi, parametre çıkarımı",
    features: [
      "AI ile otomatik şablon eşleştirme",
      "Bağlamsal anlama",
      "Takip soruları",
      "Öğrenen sistem",
    ],
  },
  {
    phase: "Phase 3",
    status: "planned",
    title: "Advanced Analytics",
    description: "Tahminleme ve trend analizi",
    features: [
      "Gelecek projeksiyonları",
      "Anomali tespiti",
      "Benchmark karşılaştırma",
      "Otomatik raporlama",
    ],
  },
];

export default function PulsePageClient() {
  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-20 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-400 to-green-500 text-white rounded-full text-sm font-bold mb-6 shadow-lg shadow-green-200">
                <Sparkles className="w-4 h-4" />
                YENİ - Şimdi Kullanıma Hazır
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6">
                İK Verilerinizle{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Konuşun
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                İKAI Pulse ile doğal dilde sorular sorun, anında içgörüler alın.
                &quot;Bu ay kaç kişi izin kullandı?&quot; gibi sorulara 2
                saniyede yanıt.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-white/80 backdrop-blur rounded-2xl border border-indigo-100">
                  <div className="text-2xl md:text-3xl font-black text-indigo-600">
                    32
                  </div>
                  <div className="text-xs md:text-sm text-gray-500 font-medium">
                    Analiz Şablonu
                  </div>
                </div>
                <div className="text-center p-4 bg-white/80 backdrop-blur rounded-2xl border border-purple-100">
                  <div className="text-2xl md:text-3xl font-black text-purple-600">
                    8
                  </div>
                  <div className="text-xs md:text-sm text-gray-500 font-medium">
                    Kategori
                  </div>
                </div>
                <div className="text-center p-4 bg-white/80 backdrop-blur rounded-2xl border border-emerald-100">
                  <div className="text-2xl md:text-3xl font-black text-emerald-600">
                    &lt;2s
                  </div>
                  <div className="text-xs md:text-sm text-gray-500 font-medium">
                    Yanıt Süresi
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/register"
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg font-bold rounded-2xl shadow-xl shadow-indigo-200 transition-all hover:-translate-y-1 text-center flex items-center justify-center gap-2"
                >
                  Ücretsiz Dene
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  href="/iletisim"
                  className="px-8 py-4 bg-white text-indigo-600 text-lg font-bold rounded-2xl border-2 border-indigo-100 hover:border-indigo-300 transition-all text-center"
                >
                  Demo Talep Et
                </Link>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <Image
                src="/images/pulse-hero.png"
                alt="İKAI Pulse AI Analitik Asistan"
                width={600}
                height={450}
                className="relative rounded-3xl shadow-2xl border border-white/50"
                priority
              />
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-indigo-500/5 blur-[120px] -z-10" />
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
              8 Kategori, 32 Analiz
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Her kategori için özel hazırlanmış analiz şablonları. İhtiyacınıza
              göre seçin veya doğal dille sorun.
            </p>
          </div>

          {/* Categories Image */}
          <div className="flex justify-center mb-12">
            <Image
              src="/images/pulse-categories.png"
              alt="Pulse Kategorileri"
              width={600}
              height={400}
              className="rounded-2xl shadow-lg"
              style={{ width: "auto", height: "auto" }}
            />
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {CATEGORIES.map((cat, index) => (
              <div
                key={index}
                className="group p-6 bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all cursor-pointer"
              >
                <div
                  className={`w-12 h-12 bg-${cat.color}-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <cat.icon className={`w-6 h-6 text-${cat.color}-600`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{cat.name}</h3>
                <div className="text-sm text-gray-500 mb-3">
                  {cat.count} analiz
                </div>
                <div className="space-y-1">
                  {cat.examples.slice(0, 2).map((ex, i) => (
                    <div
                      key={i}
                      className="text-xs text-gray-400 flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      {ex}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Anında Yanıt
              </h3>
              <p className="text-gray-600">
                2 saniyeden kısa sürede sonuç. Saatler süren raporlamayı
                saniyelere indirin.
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                %100 Doğruluk
              </h3>
              <p className="text-gray-600">
                Deterministik şablonlar sayesinde hallucination yok. Her sorgu
                güvenilir sonuç verir.
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Güvenli Erişim
              </h3>
              <p className="text-gray-600">
                Sadece kendi organizasyonunuzun verilerine erişim. KVKK uyumlu
                veri işleme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Geliştirme Yol Haritası
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Pulse sürekli gelişiyor. İşte önümüzdeki adımlar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ROADMAP.map((phase, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-3xl border ${
                  phase.status === "completed"
                    ? "bg-emerald-900/20 border-emerald-500/30"
                    : phase.status === "in-progress"
                      ? "bg-indigo-900/20 border-indigo-500/30"
                      : "bg-gray-800/50 border-gray-700"
                }`}
              >
                {/* Status Badge */}
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-4 ${
                    phase.status === "completed"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : phase.status === "in-progress"
                        ? "bg-indigo-500/20 text-indigo-400"
                        : "bg-gray-700 text-gray-400"
                  }`}
                >
                  {phase.status === "completed" && (
                    <CheckCircle2 className="w-3 h-3" />
                  )}
                  {phase.status === "in-progress" && (
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
                  )}
                  {phase.phase}
                </div>

                <h3 className="text-xl font-bold text-white mb-2">
                  {phase.title}
                </h3>
                <p className="text-gray-400 mb-6">{phase.description}</p>

                <ul className="space-y-2">
                  {phase.features.map((feature, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-sm text-gray-300"
                    >
                      <CheckCircle2
                        className={`w-4 h-4 ${
                          phase.status === "completed"
                            ? "text-emerald-500"
                            : "text-gray-600"
                        }`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            İK Analitiğinin Geleceğine Hazır Mısınız?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            14 gün ücretsiz deneme ile Pulse&apos;u keşfedin. Kredi kartı
            gerekmez.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/register"
              className="px-8 py-4 bg-white text-indigo-600 text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
            >
              Ücretsiz Başla
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              href="/features"
              className="px-8 py-4 bg-transparent text-white text-lg font-bold rounded-2xl border-2 border-white/30 hover:border-white transition-all"
            >
              Tüm Özellikleri Gör
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
